import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Eye, EyeOff, Edit, Trash2 } from "lucide-react";
import SmartPagination from "@/components/ui/smart-pagination";
import { useArticleStore } from "@/stores/articleStore";
import { usePermissions } from "@/hooks/usePermissions";
import { useExternalStore } from "@/stores/externalStore";
import { useAuthorStore } from "@/stores/authorStore";
import PermissionGuard from "@/components/PermissionGuard";
import { useDebounce } from "@/hooks/useDebounce";
import { Article } from "@/types/article";

// Modal components
import { ModalDeleteArticle } from "@/components/article/ModalDeleteArticle";
import { ModalViewArticle } from "@/components/article/ModalViewArticle";
import ModalAddArticle from "@/components/article/ModalAddArticle";
import ModalBulkAssignSeries from "@/components/article/ModalBulkAssignSeries";

const COUNTRIES: Record<string, string> = {
  "trung-quoc": "Trung Quốc",
  "han-quoc": "Hàn Quốc",
  "nhat-ban": "Nhật Bản",
  "thai-lan": "Thái Lan",
  "au-my": "Âu Mỹ",
  "hong-kong": "Hồng Kông",
  "dai-loan": "Đài Loan",
  "viet-nam": "Việt Nam",
  "an-do": "Ấn Độ",
  philippines: "Philippines",
};

const CATEGORIES = [
  {
    _id: "68a99a2eb93ed0bb896804b7",
    name: "Cổ Trang",
    url: "co-trang",
    createdAt: "2025-08-23T10:38:38.444Z",
    updatedAt: "2025-08-28T05:33:16.171Z",
  },
  {
    _id: "68a53fa390252e79fc57473c",
    name: "Hình Sự",
    url: "hinh-su",
    createdAt: "2025-08-20T03:23:15.405Z",
    updatedAt: "2025-08-28T05:33:39.231Z",
  },
  {
    _id: "68a6e59459bff37a4aef2cbc",
    name: "Hành Động",
    url: "hanh-dong",
    createdAt: "2025-08-21T09:23:32.477Z",
    updatedAt: "2025-08-28T05:33:26.453Z",
  },
  {
    _id: "68a545a6015df055c10f4308",
    name: "Kinh Dị",
    url: "kinh-di",
    createdAt: "2025-08-20T03:48:54.892Z",
    updatedAt: "2025-08-28T05:33:35.035Z",
  },
  {
    _id: "68b02d1060e3339129c2e9e9",
    name: "Chiến Tranh",
    url: "chien-tranh",
    createdAt: "2025-08-28T10:18:56.398Z",
    updatedAt: "2025-08-28T10:18:56.398Z",
  },
  {
    _id: "690b08804a0da0a6a3e9d5fe",
    name: "Tài Liệu",
    url: "tai-lieu",
    createdAt: "2025-11-05T15:19:12.506Z",
    updatedAt: "2025-11-05T15:19:12.506Z",
  },
  {
    _id: "68a6eea534694545ea4d15d1",
    name: "Viễn Tưởng",
    url: "vien-tuong",
    createdAt: "2025-08-21T10:02:13.109Z",
    updatedAt: "2025-08-28T05:33:19.225Z",
  },
  {
    _id: "68d3e390860c1a755bf0b980",
    name: "Huyền Huyễn",
    url: "huyen-huyen",
    createdAt: "2025-09-24T12:26:56.208Z",
    updatedAt: "2025-09-24T12:26:56.208Z",
  },
  {
    _id: "68a53fa390252e79fc57473f",
    name: "Chính Kịch",
    url: "chinh-kich",
    createdAt: "2025-08-20T03:23:15.407Z",
    updatedAt: "2025-08-28T05:33:37.087Z",
  },
  {
    _id: "68b0381c60e3339129c4a556",
    name: "Lịch Sử",
    url: "lich-su",
    createdAt: "2025-08-28T11:06:04.462Z",
    updatedAt: "2025-08-28T11:06:04.462Z",
  },
  {
    _id: "690b08804a0da0a6a3e9d5f7",
    name: "Giả Tưởng",
    url: "gia-tuong",
    createdAt: "2025-11-05T15:19:12.448Z",
    updatedAt: "2025-11-05T15:19:12.448Z",
  },
  {
    _id: "68a6e59459bff37a4aef2cbf",
    name: "Phiêu Lưu",
    url: "phieu-luu",
    createdAt: "2025-08-21T09:23:32.478Z",
    updatedAt: "2025-08-28T05:33:24.098Z",
  },
  {
    _id: "690b08804a0da0a6a3e9d5fd",
    name: "Phim Nhạc",
    url: "phim-nhac",
    createdAt: "2025-11-05T15:19:12.502Z",
    updatedAt: "2025-11-05T15:19:12.502Z",
  },
  {
    _id: "68aaf1a1b93ed0bb896939c7",
    name: "Gia Đình",
    url: "gia-dinh",
    createdAt: "2025-08-24T11:04:01.868Z",
    updatedAt: "2025-08-28T05:33:14.669Z",
  },
  {
    _id: "690b08804a0da0a6a3e9d5f8",
    name: "Khoa Học Viễn Tưởng",
    url: "khoa-hoc-vien-tuong",
    createdAt: "2025-11-05T15:19:12.468Z",
    updatedAt: "2025-11-05T15:19:12.468Z",
  },
  {
    _id: "68a6e67cecfbb412ded096f3",
    name: "Võ Thuật",
    url: "vo-thuat",
    createdAt: "2025-08-21T09:27:24.002Z",
    updatedAt: "2025-08-28T05:33:21.399Z",
  },
  {
    _id: "68a696dc5eb12c195549fba4",
    name: "Hài Hước",
    url: "hai-huoc",
    createdAt: "2025-08-21T03:47:40.216Z",
    updatedAt: "2025-08-28T05:33:28.016Z",
  },
  {
    _id: "68ba6b8b60e3339129e5b42c",
    name: "Bí Ẩn",
    url: "bi-an",
    createdAt: "2025-09-05T04:48:11.707Z",
    updatedAt: "2025-09-05T04:48:11.707Z",
  },
  {
    _id: "68a696dc5eb12c195549fba1",
    name: "Tâm Lý",
    url: "tam-ly",
    createdAt: "2025-08-21T03:47:40.210Z",
    updatedAt: "2025-08-28T05:33:31.023Z",
  },
  {
    _id: "68b0380860e3339129c4a52c",
    name: "Lãng Mạn",
    url: "lang-man",
    createdAt: "2025-08-28T11:05:44.030Z",
    updatedAt: "2025-08-28T11:05:44.030Z",
  },
  {
    _id: "69042013a0bd755f2b3c4b2a",
    name: "Siêu Nhiên",
    url: "sieu-nhien",
    createdAt: "2025-10-31T02:33:55.201Z",
    updatedAt: "2025-10-31T02:33:55.201Z",
  },
  {
    _id: "690b08804a0da0a6a3e9d5f6",
    name: "Gây Cấn",
    url: "gay-can",
    createdAt: "2025-11-05T15:19:12.442Z",
    updatedAt: "2025-11-05T15:19:12.442Z",
  },
  {
    _id: "68d3e661687ca8587d4c222e",
    name: "Hhkungfu",
    url: "hhkungfu",
    createdAt: "2025-09-24T12:38:57.493Z",
    updatedAt: "2025-09-24T12:38:57.493Z",
  },
  {
    _id: "68d3e390860c1a755bf0b96f",
    name: "Cn Animation",
    url: "cn-animation",
    createdAt: "2025-09-24T12:26:56.201Z",
    updatedAt: "2025-09-24T12:26:56.201Z",
  },
  {
    _id: "68d3edca7c70cb5e57257ccc",
    name: "Tiên Hiệp",
    url: "tien-hiep",
    createdAt: "2025-09-24T13:10:34.615Z",
    updatedAt: "2025-09-24T13:10:34.615Z",
  },
  {
    _id: "68a696dc5eb12c195549fb9e",
    name: "Tình Cảm",
    url: "tinh-cam",
    createdAt: "2025-08-21T03:47:40.201Z",
    updatedAt: "2025-08-28T05:33:32.547Z",
  },
  {
    _id: "68a6eea534694545ea4d15d4",
    name: "Khoa Học",
    url: "khoa-hoc",
    createdAt: "2025-08-21T10:02:13.114Z",
    updatedAt: "2025-08-28T05:33:17.724Z",
  },
  {
    _id: "690b08804a0da0a6a3e9d5fc",
    name: "Phim Hài",
    url: "phim-hai",
    createdAt: "2025-11-05T15:19:12.497Z",
    updatedAt: "2025-11-05T15:19:12.497Z",
  },
];

export default function ArticlePage() {
  const navigate = useNavigate();
  const {
    articles,
    pagination,
    isLoading,
    fetchArticles,
    setCurrentPage,
    setSelectedArticle,
    clearSelectedArticle,
    toggleDisplay,
  } = useArticleStore();

  const { canRead } = usePermissions();
  const { fetchCategories } = useExternalStore();
  const { authors, fetchAuthors } = useAuthorStore();

  // Search and filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter] = useState<string>("_all");
  const [visibilityFilter, setVisibilityFilter] = useState<string>("_all");
  const [authorFilter, setAuthorFilter] = useState<string>("_all");
  const [categoryFilter, setCategoryFilter] = useState<string>("_all");
  const [displayFilter] = useState<string>("_all"); // _all | true | false
  const [airingDayFilter, setAiringDayFilter] = useState<string>("_all");
  const [movieTypeFilter, setMovieTypeFilter] = useState<string>("_all");
  const [movieStatusFilter, setMovieStatusFilter] = useState<string>("_all");
  const [countryFilter, setCountryFilter] = useState<string>("_all");
  const [limitFilter, setLimitFilter] = useState<number>(20);

  const debouncedSearch = useDebounce(searchTerm, 500);
  const [, startTransition] = useTransition();
  const deferredArticles = useDeferredValue(articles);
  const searchRafRef = useRef<number | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [, setSelectedArticleLocal] = useState<Article | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isBulkAssignOpen, setIsBulkAssignOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Fetch articles on mount and when filters change
  useEffect(() => {
    fetchCategories();
    fetchAuthors({ page: 1, limit: 1000 });
  }, [fetchCategories, fetchAuthors]);

  // Build query params memoized
  const queryParams = useMemo(() => {
    const statusParam =
      statusFilter === "_all"
        ? undefined
        : (statusFilter as "draft" | "published" | "archived");
    const visibilityParam =
      visibilityFilter === "_all"
        ? undefined
        : (visibilityFilter as "public" | "private");
    return {
      page: pagination.page,
      limit: limitFilter,
      search: debouncedSearch || undefined,
      status: statusParam,
      visibility: visibilityParam,
      author: authorFilter === "_all" ? undefined : authorFilter,
      category: categoryFilter === "_all" ? undefined : [categoryFilter],
      isDisplay:
        displayFilter === "true"
          ? true
          : displayFilter === "false"
          ? false
          : undefined,
      airingDay: airingDayFilter === "_all" ? undefined : airingDayFilter,
      movieType:
        movieTypeFilter === "_all"
          ? undefined
          : (movieTypeFilter as "single" | "series" | "cinema" | "anime"),
      movieStatus:
        movieStatusFilter === "_all"
          ? undefined
          : (movieStatusFilter as "ongoing" | "complete"),
      country: countryFilter === "_all" ? undefined : countryFilter,
    } as const;
  }, [
    debouncedSearch,
    statusFilter,
    visibilityFilter,
    authorFilter,
    categoryFilter,
    displayFilter,
    airingDayFilter,
    movieTypeFilter,
    movieStatusFilter,
    countryFilter,
    pagination.page,
    limitFilter,
  ]);

  // Fetch articles on mount and when filters/page change
  useEffect(() => {
    startTransition(() => {
      fetchArticles({ ...queryParams });
    });
  }, [queryParams, fetchArticles]);

  // Handle search
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
  }, [setCurrentPage]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1); // Reset về trang 1 khi thay đổi search
  };

  useEffect(() => {
    return () => {
      if (searchRafRef.current) {
        cancelAnimationFrame(searchRafRef.current);
        searchRafRef.current = null;
      }
    };
  }, []);

  // Handle pagination
  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  // Handle limit change
  const handleLimitChange = useCallback(
    (limit: number) => {
      setLimitFilter(limit);
      setCurrentPage(1); // Reset về trang 1 khi thay đổi limit
    },
    [setCurrentPage]
  );

  // Handle filter changes
  const handleVisibilityFilterChange = useCallback(
    (value: string) => {
      setVisibilityFilter(value);
      setCurrentPage(1); // Reset về trang 1 khi thay đổi filter
    },
    [setCurrentPage]
  );

  const handleAuthorFilterChange = useCallback(
    (value: string) => {
      setAuthorFilter(value);
      setCurrentPage(1); // Reset về trang 1 khi thay đổi filter
    },
    [setCurrentPage]
  );

  const handleCategoryFilterChange = useCallback(
    (value: string) => {
      setCategoryFilter(value);
      setCurrentPage(1); // Reset về trang 1 khi thay đổi filter
    },
    [setCurrentPage]
  );

  const handleAiringDayFilterChange = useCallback(
    (value: string) => {
      setAiringDayFilter(value);
      setCurrentPage(1); // Reset về trang 1 khi thay đổi filter
    },
    [setCurrentPage]
  );

  const handleMovieTypeFilterChange = useCallback(
    (value: string) => {
      setMovieTypeFilter(value);
      setCurrentPage(1); // Reset về trang 1 khi thay đổi filter
    },
    [setCurrentPage]
  );

  const handleMovieStatusFilterChange = useCallback(
    (value: string) => {
      setMovieStatusFilter(value);
      setCurrentPage(1); // Reset về trang 1 khi thay đổi filter
    },
    [setCurrentPage]
  );

  const handleCountryFilterChange = useCallback(
    (value: string) => {
      setCountryFilter(value);
      setCurrentPage(1); // Reset về trang 1 khi thay đổi filter
    },
    [setCurrentPage]
  );

  const articlesList = useMemo(() => {
    if (isLoading) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 dark:text-gray-400 mt-2">Đang tải...</p>
        </div>
      );
    }
    if (deferredArticles.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-400 dark:text-gray-500 text-lg mb-2">
            Không có phim nào
          </div>
          <div className="text-gray-500 dark:text-gray-400">
            Thử thay đổi bộ lọc hoặc tạo phim mới
          </div>
        </div>
      );
    }
    return (
      <>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur supports-[backdrop-filter]:bg-gray-50/60 dark:supports-[backdrop-filter]:bg-gray-800/60">
              <tr>
                <th className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={
                      selectedIds.length > 0 &&
                      selectedIds.length === deferredArticles.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(deferredArticles.map((a) => a._id));
                      } else {
                        setSelectedIds([]);
                      }
                    }}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Hình ảnh
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Đường dẫn
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Tên phim
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Mô tả
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Thể loại
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Nguồn
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Hành động
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {deferredArticles.map((article) => {
                const statusLabel = getStatusLabel(article.visibility);
                const statusVariant = getStatusBadgeVariant(article.visibility);
                const description = article.film_description
                  ? article.film_description
                  : article.pageDescription
                  ? article.pageDescription
                  : article.content
                      ?.replace(/<[^>]*>/g, "")
                      ?.substring(0, 150) + "...";
                return (
                  <tr
                    key={article._id}
                    className="transition-all hover:bg-gray-50/60 dark:hover:bg-gray-800/60"
                  >
                    <td className="px-3 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(article._id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedIds((prev) =>
                            checked
                              ? Array.from(new Set([...prev, article._id]))
                              : prev.filter((id) => id !== article._id)
                          );
                        }}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {article.featuredImage?.url ? (
                        <img
                          src={article.featuredImage.url}
                          alt={article.featuredImage.alt || article.title}
                          className="w-14 h-14 object-cover rounded"
                        />
                      ) : (
                        <div className="w-14 h-14 bg-gray-100 rounded" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 truncate max-w-[240px]">
                      <Link to={`/articles/${article._id}/edit`}>
                        /{article.slug}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white truncate max-w-[220px]">
                      {(article as any).movieData?.name ||
                        article.movie?.title ||
                        "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 truncate max-w-[320px]">
                      {description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {(article as any).categoryData?.name ||
                        article.category
                          ?.map((category) => category.name)
                          .join(", ") ||
                        "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {article.source ? (
                        <Badge
                          className={
                            article.source === "Ophim"
                              ? "bg-blue-100 text-blue-800"
                              : article.source === "OPhim"
                              ? "bg-pink-100 text-pink-800"
                              : "bg-purple-100 text-purple-800"
                          }
                        >
                          {article.source}
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge
                        variant={statusVariant}
                        className={
                          article.visibility === "private"
                            ? "bg-red-200 text-red-700"
                            : "bg-blue-200 text-blue-700"
                        }
                      >
                        {statusLabel}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard subject="article" action="read">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleView(article)}
                            className="cursor-pointer"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard subject="article" action="update">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(article)}
                            className="cursor-pointer"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard subject="article" action="update">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleDisplay(article)}
                            className="cursor-pointer"
                            title={
                              article.isDisplay
                                ? "Ẩn khỏi tin tức & tìm kiếm (vẫn xem được qua URL)"
                                : "Hiện lại trên tin tức & tìm kiếm"
                            }
                          >
                            {article.isDisplay ? (
                              <Eye className="w-4 h-4" />
                            ) : (
                              <EyeOff className="w-4 h-4 text-orange-500" />
                            )}
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard subject="article" action="delete">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(article)}
                            className="cursor-pointer text-red-600 hover:text-red-700 hover:border-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <SmartPagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          showLimitSelector={true}
          limit={limitFilter}
          onLimitChange={handleLimitChange}
          limitOptions={[10, 20, 50, 100]}
        />
      </>
    );
  }, [
    deferredArticles,
    isLoading,
    pagination,
    handlePageChange,
    limitFilter,
    handleLimitChange,
    selectedIds,
  ]);

  // Handle actions
  const handleView = (article: Article) => {
    setSelectedArticleLocal(article);
    setSelectedArticle(article);
    setIsViewModalOpen(true);
  };

  const handleEdit = (article: Article) => {
    navigate(`/articles/${article._id}/edit`);
  };

  const handleToggleDisplay = (article: Article) => {
    toggleDisplay(article._id);
  };

  const handleDelete = (article: Article) => {
    setSelectedArticleLocal(article);
    setSelectedArticle(article);
    setIsDeleteModalOpen(true);
  };

  // Hành động đổi trạng thái/hiển thị hiện không sử dụng trong UI; có thể bật lại khi cần

  // Get status badge variant
  function getStatusBadgeVariant(status: string) {
    switch (status) {
      case "public":
        return "default";
      case "private":
        return "secondary";
      default:
        return "secondary";
    }
  }

  // Get status label
  function getStatusLabel(status: string) {
    switch (status) {
      case "public":
        return "Công khai";
      case "private":
        return "Riêng tư";

      default:
        return status;
    }
  }

  // Check if user can access page
  if (!canRead("article")) {
    return (
      <DashboardLayout>
        <div className="p-10 text-center">
          <div className="text-red-600 text-lg font-medium mb-2">
            Không có quyền truy cập
          </div>
          <div className="text-gray-500">
            Bạn không có quyền truy cập trang quản lý phim
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
                Quản lý phim
              </h1>
              <div className="mt-2 flex items-center gap-2">
                <p className="text-gray-500 dark:text-gray-400">
                  Quản lý và xuất bản các phim trên website
                </p>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                  {pagination.total} phim
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <PermissionGuard subject="article" action="update">
                <Button
                  onClick={() => setIsBulkAssignOpen(true)}
                  variant="outline"
                  disabled={selectedIds.length === 0}
                  className="cursor-pointer"
                >
                  Gán series
                </Button>
              </PermissionGuard>
              <PermissionGuard subject="article" action="create">
                <Button
                  onClick={() => setIsAddModalOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 cursor-pointer"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Thêm phim mới
                </Button>
              </PermissionGuard>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Bộ lọc và tìm kiếm</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Tìm kiếm
                </label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Tìm theo tiêu đề, nội dung..."
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    className="flex-1"
                    type="text"
                  />
                  <Button onClick={handleSearch} className="cursor-pointer">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Hiển thị
                </label>
                <Select
                  value={visibilityFilter}
                  onValueChange={handleVisibilityFilterChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Tất cả" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Tất cả</SelectItem>
                    <SelectItem value="public">Công khai</SelectItem>
                    <SelectItem value="private">Riêng tư</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Tác giả
                </label>
                <Select
                  value={authorFilter}
                  onValueChange={handleAuthorFilterChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Tất cả tác giả" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Tất cả tác giả</SelectItem>
                    {authors.map((a) => (
                      <SelectItem key={a._id} value={a._id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Thể loại
                </label>
                <Select
                  value={categoryFilter}
                  onValueChange={handleCategoryFilterChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Tất cả thể loại" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto scrollbar-gentle">
                    <SelectItem value="_all">Tất cả thể loại</SelectItem>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c._id} value={c._id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Second row of filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Ngày chiếu
                </label>
                <Select
                  value={airingDayFilter}
                  onValueChange={handleAiringDayFilterChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Tất cả ngày" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Tất cả ngày</SelectItem>
                    <SelectItem value="Thứ 2">Thứ 2</SelectItem>
                    <SelectItem value="Thứ 3">Thứ 3</SelectItem>
                    <SelectItem value="Thứ 4">Thứ 4</SelectItem>
                    <SelectItem value="Thứ 5">Thứ 5</SelectItem>
                    <SelectItem value="Thứ 6">Thứ 6</SelectItem>
                    <SelectItem value="Thứ 7">Thứ 7</SelectItem>
                    <SelectItem value="Chủ nhật">Chủ nhật</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Loại phim
                </label>
                <Select
                  value={movieTypeFilter}
                  onValueChange={handleMovieTypeFilterChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Tất cả loại" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Tất cả loại</SelectItem>
                    <SelectItem value="single">Phim lẻ</SelectItem>
                    <SelectItem value="series">Phim bộ</SelectItem>
                    <SelectItem value="cinema">Phim chiếu rạp</SelectItem>
                    <SelectItem value="anime">Hoạt hình/Anime</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Trạng thái phim
                </label>
                <Select
                  value={movieStatusFilter}
                  onValueChange={handleMovieStatusFilterChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Tất cả trạng thái" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Tất cả trạng thái</SelectItem>
                    <SelectItem value="ongoing">Đang chiếu</SelectItem>
                    <SelectItem value="complete">Hoàn thành</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Quốc gia
                </label>
                <Select
                  value={countryFilter}
                  onValueChange={handleCountryFilterChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Tất cả quốc gia" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto scrollbar-gentle">
                    <SelectItem value="_all">Tất cả quốc gia</SelectItem>
                    {Object.entries(COUNTRIES).map(([slug, name]) => (
                      <SelectItem key={slug} value={slug}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Articles List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                Danh sách phim
                <Badge variant="secondary" className="ml-2">
                  {pagination.total} phim
                </Badge>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>{articlesList}</CardContent>
        </Card>

        <ModalDeleteArticle
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            clearSelectedArticle();
          }}
          onComplete={() => {
            setIsDeleteModalOpen(false);
            clearSelectedArticle();
            // Refresh articles
            fetchArticles({
              page: pagination.page,
              limit: limitFilter,
              search: debouncedSearch || undefined,
              status:
                statusFilter === "_all"
                  ? undefined
                  : (statusFilter as "draft" | "published" | "archived"),
              visibility:
                visibilityFilter === "_all"
                  ? undefined
                  : (visibilityFilter as "public" | "private"),
              author: authorFilter === "_all" ? undefined : authorFilter,
              category:
                categoryFilter === "_all" ? undefined : [categoryFilter],
              isDisplay:
                displayFilter === "true"
                  ? true
                  : displayFilter === "false"
                  ? false
                  : undefined,
              airingDay:
                airingDayFilter === "_all" ? undefined : airingDayFilter,
              movieType:
                movieTypeFilter === "_all"
                  ? undefined
                  : (movieTypeFilter as
                      | "single"
                      | "series"
                      | "cinema"
                      | "anime"),
              movieStatus:
                movieStatusFilter === "_all"
                  ? undefined
                  : (movieStatusFilter as "ongoing" | "complete"),
              country: countryFilter === "_all" ? undefined : countryFilter,
            });
          }}
        />

        <ModalViewArticle
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            clearSelectedArticle();
          }}
        />

        <ModalAddArticle
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onCompleted={() => {
            setIsAddModalOpen(false);
            clearSelectedArticle();
            fetchArticles({
              page: pagination.page,
              limit: limitFilter,
            });
            fetchCategories();
            fetchAuthors({ page: 1, limit: 1000 });
          }}
        />

        <ModalBulkAssignSeries
          isOpen={isBulkAssignOpen}
          onClose={() => setIsBulkAssignOpen(false)}
          selectedArticles={articles.filter((a) => selectedIds.includes(a._id))}
          onCompleted={() => {
            setIsBulkAssignOpen(false);
            setSelectedIds([]);
            fetchArticles({ ...queryParams });
          }}
        />
      </div>
    </DashboardLayout>
  );
}
